/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useRef, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";

// Soft UI Dashboard PRO React helper functions
import gradientChartLine from "assets/theme/functions/gradientChartLine";

// MixedChart configurations
import configs from "examples/Charts/MixedChart/configs";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";

const dashboardSelectSx = {
  "--soft-select-height": "28px",
  "--soft-select-value-height": "26px",
  "--soft-select-font-size": "10px",
  "--soft-select-value-px": "8px",
  "--soft-select-text-top": "50%",
  "--soft-select-indicator-padding": "4px",
  "--soft-select-indicator-size": "12px",
  "--soft-select-menu-padding": "4px",
  "--soft-select-option-padding": "4px 8px",
  "--soft-select-border-radius": "6px",
};

const dashboardHeadingSx = {
  fontSize: "12px !important",
  fontWeight: "400 !important",
  lineHeight: "1.15 !important",
};

const dashboardDataSx = {
  fontSize: "10px !important",
  fontWeight: "400 !important",
  lineHeight: "1.15 !important",
};

const dashboardCardSx = {
  width: "100%",
  height: "100%",
};

function MixedChart({ title = "", description = "", height = "19.125rem", chart, range, setRange }) {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState({});
  const { data, options } = chartData;

  const option = [
    { value: "24h", label: "24 Hours" },
    { value: "5d", label: "5 Days" },
  ];

  useEffect(() => {
    const chartDatasets = chart.datasets
      ? chart.datasets.map((dataset) => {
          let finalConfigs;

          const defaultLine = {
            ...dataset,
            type: "line",
            tension: 0.4,
            borderWidth: 3,
            pointRadius: 2,
            pointBackgroundColor: colors[dataset.color]
              ? colors[dataset.color || "dark"].main
              : colors.dark.main,
            borderColor: colors[dataset.color]
              ? colors[dataset.color || "dark"].main
              : colors.dark.main,
            maxBarThickness: 6,
          };

          const gradientLine = {
            ...dataset,
            type: "line",
            tension: 0.4,
            pointRadius: 0,
            borderWidth: 3,
            borderColor: colors[dataset.color]
              ? colors[dataset.color || "dark"].main
              : colors.dark.main,
            fill: true,
            maxBarThickness: 6,
            backgroundColor: gradientChartLine(
              chartRef.current.children[0],
              colors[dataset.color] ? colors[dataset.color || "dark"].main : colors.dark.main
            ),
          };

          const bar = {
            ...dataset,
            type: "bar",
            weight: 5,
            borderWidth: 0,
            borderRadius: 4,
            backgroundColor: colors[dataset.color]
              ? colors[dataset.color || "dark"].main
              : colors.dark.main,
            fill: false,
            maxBarThickness: 35,
          };

          const thinBar = {
            ...dataset,
            type: "bar",
            weight: 5,
            borderWidth: 0,
            borderRadius: 4,
            backgroundColor: colors[dataset.color]
              ? colors[dataset.color || "dark"].main
              : colors.dark.main,
            fill: false,
            maxBarThickness: 10,
          };

          if (dataset.chartType === "default-line") {
            finalConfigs = defaultLine;
          } else if (dataset.chartType === "gradient-line") {
            finalConfigs = gradientLine;
          } else if (dataset.chartType === "thin-bar") {
            finalConfigs = thinBar;
          } else {
            finalConfigs = bar;
          }

          return { ...finalConfigs };
        })
      : [];

    setChartData(configs(chart.labels || [], chartDatasets, true));
  }, [chart]);

  const renderChart = (
    <SoftBox p={1} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {title || description ? (
        <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
          {title && (
            <SoftBox
              mb={1.5}
              minHeight="28px"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <SoftTypography variant="h6" fontWeight="regular" sx={dashboardHeadingSx}>
                {title}
              </SoftTypography>

              <Grid sx={{ minWidth: 140 }}>
                <SoftSelect
                  size="small"
                  compact
                  placeholder="Select"
                  sx={dashboardSelectSx}
                  value={option.find((opt) => opt.value === range)}
                  options={option}
                  onChange={(option) => setRange(option.value)}
                />
              </Grid>
            </SoftBox>
          )}
          {description && (
            <SoftBox mb={2}>
              <SoftTypography
                component="div"
                variant="button"
                fontWeight="regular"
                color="text"
                sx={dashboardDataSx}
              >
                {description}
              </SoftTypography>
            </SoftBox>
          )}
        </SoftBox>
      ) : null}
      {useMemo(
        () => (
          <SoftBox ref={chartRef} sx={{ height, minHeight: height }}>
            <Line data={data} options={options} />
          </SoftBox>
        ),
        [chartData, height]
      )}
    </SoftBox>
  );

  return title || description ? <Card sx={dashboardCardSx}>{renderChart}</Card> : renderChart;
}

// Typechecking props for the MixedChart
MixedChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
  range: PropTypes.string.isRequired,
  setRange: PropTypes.func.isRequired,
};

export default MixedChart;
